import React, { useMemo, useState } from 'react';
import Toggle from './Toggle';
import CopyLinkButton from '../shared/shareLinks';

// Vouchers tab — table of every voucher product the salon sells.
//
// Built on a real HTML <table> rather than CSS grid because native
// table cell alignment (vertical-align: middle) just works across
// every viewport width — same pattern as Beu's clients / staff /
// stocktake tables. Rows are role="button" so the whole row is
// clickable (opens the editor in phase 2).

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="7" cy="7" r="5" />
    <path d="M10.5 10.5L14 14" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3l5 4-5 4" />
  </svg>
);

// Summary line for the NAME column: combines expiry + channel info into
// a single readable sentence the salon owner can scan.
const buildSubLine = (v) => {
  const parts = [];
  if (v.expiry) {
    parts.push(
      v.expiry.toLowerCase() === 'never'
        ? 'Never expires'
        : `Expires after ${v.expiry.toLowerCase()}`
    );
  }
  if (v.promo != null && v.promo < v.value) parts.push('Promo pricing');
  const channels = [];
  if (v.online) channels.push('Online');
  if (v.pos) channels.push('POS');
  if (channels.length) parts.push(channels.join(' + '));
  return parts.join(' · ');
};

const VouchersPage = ({ vouchers, currency, channels, onSetChannel, onView, onNew }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vouchers.filter((v) => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (!q) return true;
      return v.name.toLowerCase().includes(q);
    });
  }, [vouchers, search, statusFilter]);

  // Row click handler shared by mouse + keyboard for accessibility.
  // Opens the read-only detail view (which then offers an Edit action).
  // Explicit null check rather than `onView?.(v)` because the project's
  // ESLint flags optional-chain calls at statement position.
  const activate = (v) => { if (onView) onView(v); };
  const handleRowActivate = (v) => () => activate(v);
  const handleRowKeyDown = (v) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate(v);
    }
  };

  return (
    <>
      {/* Salon-wide selling channels — master switches over every voucher's own
          online/POS settings. Off hides that channel everywhere; each voucher
          keeps its own remembered setting for when it's switched back on. */}
      {channels && onSetChannel && (
        <div className="v2-channels-bar">
          <div className="v2-channels-bar-info">
            <span className="v2-channels-bar-label">Selling gift cards</span>
            <span className="v2-channels-bar-hint">
              Master switch for the whole salon. Turning one off hides it everywhere, but each voucher keeps its own setting.
            </span>
          </div>
          <div className="v2-channels-bar-toggles">
            <div className={`v2-channels-toggle ${channels.online ? '' : 'is-off'}`}>
              <span className="v2-channels-toggle-label">Online</span>
              <Toggle
                on={channels.online}
                onChange={(on) => onSetChannel('online', on)}
                ariaLabel="Sell gift cards online"
              />
            </div>
            <div className={`v2-channels-toggle ${channels.pos ? '' : 'is-off'}`}>
              <span className="v2-channels-toggle-label">In-store</span>
              <Toggle
                on={channels.pos}
                onChange={(on) => onSetChannel('pos', on)}
                ariaLabel="Sell gift cards in-store"
              />
            </div>
          </div>
        </div>
      )}

      <div className="v2-toolbar">
        <div className="v2-search">
          <span className="v2-search-icon"><SearchIcon /></span>
          <input
            type="text"
            placeholder="Search vouchers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="v2-filter-chips">
          {['all', 'active', 'draft'].map((s) => (
            <button
              key={s}
              type="button"
              className={`v2-filter-chip ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s === 'active' ? 'Active' : 'Draft'}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        vouchers.length === 0 ? (
          // True empty library (brand-new salon) — offer a create CTA rather
          // than filter-clearing advice that doesn't apply.
          <div className="v2-page-stub">
            <div className="v2-page-stub-title">No vouchers yet</div>
            <div className="v2-page-stub-sub">
              Create your first voucher so clients can buy gift cards online or in-store.
            </div>
            {onNew && (
              <button type="button" className="v2-btn-primary" style={{ marginTop: 16 }} onClick={onNew}>
                New voucher
              </button>
            )}
          </div>
        ) : (
          <div className="v2-page-stub">
            <div className="v2-page-stub-title">No vouchers match</div>
            <div className="v2-page-stub-sub">
              Try clearing the search or switching the status filter.
            </div>
          </div>
        )
      ) : (
        <div className="v2-table-wrap">
          <table className="v2-table">
            <colgroup>
              <col className="v2-col-from" />
              <col className="v2-col-name" />
              <col className="v2-col-denoms" />
              <col className="v2-col-sold" />
              <col className="v2-col-status" />
              <col className="v2-col-chev" />
            </colgroup>
            <thead>
              <tr>
                <th>From</th>
                <th>Name</th>
                <th>Denominations</th>
                <th>Sold</th>
                <th>Status</th>
                <th aria-hidden />
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const lowest = v.denominations?.length
                  ? Math.min(...v.denominations)
                  : v.value;
                const hasCustom = !!v.customAmount;
                const denoms = v.denominations || [];
                // Cap visible chips at 4 (denominations + the Custom chip
                // counted together) so the row stays one chip line tall —
                // overflow surfaces as a "+N" chip.
                const customCount = hasCustom ? 1 : 0;
                const total = denoms.length + customCount;
                const MAX_VISIBLE = 4;
                let visibleDenoms = denoms;
                let extra = 0;
                if (total > MAX_VISIBLE) {
                  visibleDenoms = denoms.slice(0, MAX_VISIBLE - customCount - 1);
                  extra = total - (visibleDenoms.length + customCount);
                }

                return (
                  <tr
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    onClick={handleRowActivate(v)}
                    onKeyDown={handleRowKeyDown(v)}
                  >
                    <td className="v2-td-from">
                      <span className="v2-amt">
                        <span className="v2-amt-cur">{currency}</span>
                        <span className="v2-amt-num">{lowest}</span>
                      </span>
                    </td>

                    <td className="v2-td-name">
                      <div className="v2-cell-title">{v.name}</div>
                      <div className="v2-cell-sub">{buildSubLine(v)}</div>
                    </td>

                    <td className="v2-td-denoms">
                      <div className="v2-denoms">
                        {visibleDenoms.map((d) => (
                          <span key={d} className="v2-denom-chip">{currency}{d}</span>
                        ))}
                        {hasCustom && (
                          <span className="v2-denom-chip v2-denom-chip--custom">Custom</span>
                        )}
                        {extra > 0 && (
                          <span className="v2-denom-chip v2-denom-chip--more">+{extra}</span>
                        )}
                      </div>
                    </td>

                    <td className="v2-td-sold">
                      <span className="v2-amt v2-amt--small">
                        <span className="v2-amt-num">{v.sold ?? 0}</span>
                      </span>
                    </td>

                    <td className="v2-td-status">
                      <span className={`v2-status v2-status--${v.status === 'active' ? 'active' : 'draft'}`}>
                        {v.status === 'active' ? 'Active' : 'Draft'}
                      </span>
                    </td>

                    <td className="v2-td-chev">
                      <div className="v2-row-actions">
                        <CopyLinkButton type="voucher" id={v.id} className="share-link-btn--reveal share-link-btn--sm" title="Copy voucher link" />
                        <span className="v2-row-chev" aria-hidden><ChevronIcon /></span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default VouchersPage;
